/**
 * Past Boxes - Add to Cart Functionality
 * Storefront API client-side stock checking with localStorage caching
 * Product: Throwback Boxes (handle: throwback-box)
 */

(function () {
  'use strict';

  const _isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com');
  const _err = _isDev ? console.error.bind(console) : () => {};

  // Configuration
  const PRODUCT_HANDLE = 'throwback-box';
  const CACHE_KEY = 'past-boxes-stock-';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const STOREFRONT_API_URL = window.Shopify?.shop
    ? `https://${window.Shopify.shop}/api/2025-01/graphql.json`
    : `https://${document.location.hostname}/api/2025-01/graphql.json`;

  // Get today's date key for cache
  function getCacheDateKey() {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return CACHE_KEY + dateStr;
  }

  /**
   * Query Storefront API for product inventory
   */
  async function getProductStock() {
    const cacheKey = getCacheDateKey();

    // Check localStorage cache first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < CACHE_DURATION) {
          return data.inStock;
        }
      }
    } catch (e) {
      _err('Cache read error:', e);
    }

    // Query Storefront API for real-time stock
    try {
      const query = `{
        products(first: 1, query: "handle:${PRODUCT_HANDLE}") {
          edges {
            node {
              id
              handle
              variants(first: 1) {
                edges {
                  node {
                    id
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }`;

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify?.storefront?.accessToken || '',
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`API response: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        _err('GraphQL errors:', result.errors);
        return null;
      }

      const product = result.data?.products?.edges?.[0]?.node;
      if (!product) {
        _err('Product not found');
        return null;
      }

      const variant = product.variants?.edges?.[0]?.node;
      const inStock = variant?.availableForSale ?? false;

      // Cache the result
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            inStock,
            timestamp: Date.now(),
          })
        );
      } catch (e) {
        _err('Cache write error:', e);
      }

      return inStock;
    } catch (err) {
      _err('Stock check failed:', err);
      return null;
    }
  }

  /**
   * Add product to cart via Shopify Cart API
   */
  async function addToCart(buttonElement) {
    try {
      // Get the first variant of the product
      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify?.storefront?.accessToken || '',
        },
        body: JSON.stringify({
          query: `{
            products(first: 1, query: "handle:${PRODUCT_HANDLE}") {
              edges {
                node {
                  variants(first: 1) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }`,
        }),
      });

      const result = await response.json();
      const rawId = result.data?.products?.edges?.[0]?.node?.variants?.edges?.[0]?.node?.id;

      if (!rawId) {
        _err('Could not get variant ID');
        throw new Error('Variant not found');
      }

      // Storefront API returns GID format: "gid://shopify/ProductVariant/12345"
      // Cart AJAX API (/cart/add.js) requires a numeric ID
      const numericId = rawId.includes('/') ? rawId.split('/').pop() : rawId;

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(numericId, 10),
          quantity: 1,
        }),
      });

      if (addResponse.ok) {
        // Redirect to cart
        // eslint-disable-next-line require-atomic-updates
        window.location.href = '/cart';
      } else {
        throw new Error('Add to cart failed: ' + addResponse.status);
      }
    } catch (err) {
      _err('Add to cart failed:', err);
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.textContent = 'Add to Cart';
      }
      // eslint-disable-next-line no-alert
      alert('Could not add to cart. Please try again.');
    }
  }

  /**
   * Create and inject button into a card
   */
  async function injectButtonIntoCard(card) {
    // Find the card body and border
    const cardBody = card.querySelector('.past-boxes__card-body');
    const cardBorder = card.querySelector('.past-boxes__card-border');

    if (!cardBody || !cardBorder) return;

    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'past-boxes__cart-button-container';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'past-boxes__cart-btn past-boxes__cart-btn--loading';
    button.setAttribute('aria-label', 'Add Throwback Boxes to cart');
    button.textContent = 'Checking...';
    button.disabled = true;

    buttonContainer.appendChild(button);

    // Insert after the border divider
    cardBorder.parentNode.insertBefore(buttonContainer, cardBorder.nextSibling);

    // Check stock and update button
    const inStock = await getProductStock();

    if (inStock === null) {
      // API call failed, hide button
      buttonContainer.style.display = 'none';
      return;
    }

    // Update button state
    button.classList.remove('past-boxes__cart-btn--loading');

    if (inStock) {
      button.textContent = 'Add to Cart';
      button.disabled = false;
      button.classList.add('past-boxes__cart-btn--available');

      // Add click handler
      button.addEventListener('click', function (e) {
        e.stopPropagation(); // Prevent card click handler
        button.disabled = true;
        button.textContent = 'Adding...';
        addToCart(button);
      });
    } else {
      button.textContent = 'Sold Out';
      button.disabled = true;
      button.classList.add('past-boxes__cart-btn--sold-out');
    }
  }

  /**
   * Initialize: inject buttons into all cards
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        processCards();
      });
    } else {
      processCards();
    }
  }

  function processCards() {
    const cards = document.querySelectorAll('.past-boxes__card');
    cards.forEach(function (card) {
      // Skip cards that already have a button container (prevent duplicates on refresh)
      if (card.querySelector('.past-boxes__cart-button-container')) return;
      injectButtonIntoCard(card);
    });
  }

  // Add manual refresh function for users
  window.pastBoxesRefreshStock = function () {
    const cacheKey = getCacheDateKey();
    try {
      localStorage.removeItem(cacheKey);
    } catch (_e) {}

    // Re-initialize to refresh all buttons
    processCards();
  };

  // Start the module
  init();
})();
